import React, { useMemo } from 'react';
import {
  Box,
  Card,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { EmptyState, StatCard } from '../../../components/ui';
import { DashboardSummary } from '../../../types';
import { accent, teal } from '../../../theme/tokens';
import { APP_LOCALE } from '../../../utils/locale';

/**
 * The period reports that sat on the dashboard before the workshop floor:
 * outstanding and completion rate, visits and money per day, where
 * appointments stand, and mechanic workload.
 *
 * Chart colours come from the app's own ramps and were checked with the
 * palette validator rather than picked by eye: light mode uses accent.500 with
 * teal.600, dark mode accent.400 with teal.600 — both pass the lightness band,
 * chroma floor, CVD separation and surface-contrast checks.
 *
 * The two measures (visits and money) are never put on one pair of axes; they
 * get their own charts, because a dual-axis chart lets the reader infer a
 * correlation from an arbitrary choice of scales.
 */

const currency = (value: number) =>
  value.toLocaleString(APP_LOCALE, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const currencyExact = (value: number) =>
  value.toLocaleString(APP_LOCALE, { style: 'currency', currency: 'USD' });

/**
 * "Jun 14" — short enough that the axis does not need rotated labels.
 *
 * Takes `unknown` because recharts types its axis and tooltip formatters
 * against every value a chart could hold; every call here passes the
 * "YYYY-MM-DD" key from the API.
 */
const shortDay = (iso: unknown) => {
  if (typeof iso !== 'string') return '';
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(APP_LOCALE, {
    month: 'short',
    day: 'numeric',
  });
};

/** Recharts hands tooltip values through as a union; only numbers reach these. */
const asNumber = (value: unknown): number =>
  typeof value === 'number' ? value : Number(value) || 0;

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  in_progress: 'In progress',
  completed: 'Completed',
  ready_for_pickup: 'Ready for pickup',
  closed_out: 'Closed out',
  cancelled: 'Canceled',
};

const ReportCharts: React.FC<{ summary: DashboardSummary }> = ({ summary }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Validated pairs — see the file comment.
  const series = useMemo(
    () => ({
      visits: isDark ? accent[400] : accent[500],
      money: teal[600],
    }),
    [isDark]
  );

  const axis = theme.palette.text.secondary;
  const grid = theme.palette.divider;

  const statusRows = useMemo(() => {
    return Object.entries(summary.appointments.byStatus)
      .map(([status, count]) => ({
        status,
        label: STATUS_LABELS[status] ?? status,
        count,
      }))
      .filter((row) => row.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [summary]);

  const tooltipStyle = {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    fontSize: 13,
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6" component="h2">
          Reports
        </Typography>
        <Typography variant="body2" color="text.secondary">
          For the selected period. Money is taken from the payment ledger, so these figures match
          what was actually collected.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <StatCard
            label="Outstanding"
            value={summary.revenue.outstanding}
            prefix="$"
            tone="warning"
            icon={<ReceiptLongOutlinedIcon />}
            caption="Unpaid across all issued invoices"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <StatCard
            label="Completion rate"
            value={summary.appointments.completionRate}
            suffix="%"
            tone="neutral"
            icon={<TaskAltOutlinedIcon />}
            caption="Excludes cancellations"
          />
        </Grid>
      </Grid>

      {/* Two measures, two charts — never one pair of axes */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Appointments per day
            </Typography>
            <Box sx={{ height: 260, mt: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary.daily} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke={grid} vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={shortDay}
                    tick={{ fill: axis, fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: grid }}
                    minTickGap={24}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: axis, fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    cursor={{ fill: grid, opacity: 0.35 }}
                    labelFormatter={shortDay}
                    formatter={(value: unknown) => [asNumber(value), 'Appointments']}
                  />
                  {/* Rounded data-end, anchored to the baseline */}
                  <Bar dataKey="appointments" fill={series.visits} radius={[4, 4, 0, 0]} maxBarSize={22} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Revenue collected per day
            </Typography>
            <Box sx={{ height: 260, mt: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={summary.daily} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={series.money} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={series.money} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={grid} vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={shortDay}
                    tick={{ fill: axis, fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: grid }}
                    minTickGap={24}
                  />
                  <YAxis
                    tick={{ fill: axis, fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={56}
                    tickFormatter={(value: unknown) => currency(asNumber(value))}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    labelFormatter={shortDay}
                    formatter={(value: unknown) => [currencyExact(asNumber(value)), 'Collected']}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke={series.money}
                    strokeWidth={2}
                    fill="url(#revenueFill)"
                    activeDot={{ r: 4, strokeWidth: 2, stroke: theme.palette.background.paper }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Where appointments stand
            </Typography>
            {statusRows.length === 0 ? (
              <EmptyState dense title="No appointments in this period" />
            ) : (
              <Box sx={{ height: 260, mt: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  {/* One series, so identity comes from the labelled axis
                      rather than from colour — no legend needed. */}
                  <BarChart
                    data={statusRows}
                    layout="vertical"
                    margin={{ top: 4, right: 28, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid stroke={grid} horizontal={false} />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tick={{ fill: axis, fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      tick={{ fill: axis, fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      width={110}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      cursor={{ fill: grid, opacity: 0.35 }}
                      formatter={(value: unknown) => [asNumber(value), 'Appointments']}
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={18}>
                      {statusRows.map((row) => (
                        <Cell key={row.status} fill={series.visits} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Card sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Mechanic workload
            </Typography>
            {summary.mechanics.length === 0 ? (
              <EmptyState
                dense
                title="No mechanics yet"
                description="Add staff to see how work is distributed."
              />
            ) : (
              <>
                <Box sx={{ height: 240, mt: 1 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={summary.mechanics.slice(0, 6)}
                      margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
                      barGap={2}
                    >
                      <CartesianGrid stroke={grid} vertical={false} />
                      <XAxis
                        dataKey="name"
                        tick={{ fill: axis, fontSize: 11 }}
                        tickLine={false}
                        axisLine={{ stroke: grid }}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fill: axis, fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={40}
                      />
                      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: grid, opacity: 0.35 }} />
                      {/* Two series, so a legend is always present; the
                          table below carries the same numbers as text. */}
                      <Legend
                        wrapperStyle={{ fontSize: 12, color: axis, paddingTop: 8 }}
                        iconType="circle"
                        iconSize={8}
                      />
                      <Bar dataKey="assigned" name="Assigned" fill={series.visits} radius={[4, 4, 0, 0]} maxBarSize={18} />
                      <Bar dataKey="completed" name="Finished" fill={series.money} radius={[4, 4, 0, 0]} maxBarSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                <TableContainer sx={{ mt: 1, overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Mechanic</TableCell>
                        <TableCell align="right">Assigned</TableCell>
                        <TableCell align="right">Finished</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.mechanics.map((mechanic) => (
                        <TableRow key={mechanic.id}>
                          <TableCell>{mechanic.name}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {mechanic.assigned}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {mechanic.completed}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default ReportCharts;
